/**
 * Driver Registration Page - Green & White Theme
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { Link2, ArrowLeft, Truck, Loader2 } from 'lucide-react';

const DriverRegister = () => {
  const navigate = useNavigate();
  const [vehicleType, setVehicleType] = useState('motorcycle');
  const [licensePlate, setLicensePlate] = useState('');
  const [ppvId, setPpvId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/drivers/register', {
        vehicle_type: vehicleType,
        license_plate: licensePlate,
        ppv_id: ppvId || null
      });
      toast.success('Driver registration successful!');
      navigate('/driver');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-emerald-700 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link2 className="h-6 w-6" />
          <span className="font-bold text-lg">Driver Registration</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-md mx-auto">
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Become a Driver</h1>
            <p className="text-gray-600 mt-2">Start earning with  deliveries</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-gray-700">Vehicle Type</Label>
              <Select value={vehicleType} onValueChange={setVehicleType}>
                <SelectTrigger data-testid="vehicle-type-select" className="bg-white border-gray-200 h-12 rounded-xl">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="bicycle">Bicycle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">License Plate</Label>
              <Input
                data-testid="license-plate-input"
                type="text"
                placeholder="ABC 1234"
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                className="bg-white border-gray-200 h-12 rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">PPV ID (Optional)</Label>
              <Input
                data-testid="ppv-id-input"
                type="text"
                placeholder="PPV-001"
                value={ppvId}
                onChange={(e) => setPpvId(e.target.value)}
                className="bg-white border-gray-200 h-12 rounded-xl"
              />
              <p className="text-xs text-gray-500">Public Passenger Vehicle license (if applicable)</p>
            </div>

            <Button
              type="submit"
              data-testid="register-driver-btn"
              disabled={isLoading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12 font-medium mt-4"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                'Register as Driver'
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default DriverRegister;
